import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { UserAvatar } from "@/components/ui/UserAvatar";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/client";
import {
  Car as CarIcon,
  Plus,
  MapPin,
  User,
  Calendar,
  History,
  ChevronDown,
  ChevronRight,
  CalendarDays,
  ArrowLeftRight,
  AlertTriangle,
  MessageSquare,
  Wrench,
  ShieldCheck,
  ClipboardList,
  Send,
  Trash2,
  Users,
  Pencil,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Modal } from "@/components/ui/Modal";
import { PageLoader } from "@/components/ui/Spinner";
import { DatePicker } from "@/components/ui/DatePicker";
import { formatDate } from "@/utils/formatDate";
import { useAuthStore } from "@/store/auth";
import {
  useCars,
  useCarHistory,
  useCreateCar,
  useUpdateCar,
  useDeleteCar,
  useSellCar,
  useAssignCar,
  useReturnToPool,
  useMovePool,
  useReservations,
  useCreateReservation,
  useDeleteReservation,
  useIssueReports,
  useCreateIssueReport,
  useUpdateIssueReport,
  useIssueComments,
  useCreateComment,
  useDeleteComment,
} from "../hooks/useFleet";
import {
  LOCATION_COLORS,
  LOCATION_LABELS,
  FUEL_TYPE_LABELS,
  CAR_CATEGORY_LABELS,
  OWNERSHIP_TYPE_LABELS,
  EURO_CATEGORY_LABELS,
  ISSUE_STATUS_LABELS,
  ISSUE_PRIORITY_LABELS,
  type Car,
  type CarReservation,
  type CarIssueReport,
  type CarIssueStatus,
  type CarIssuePriority,
} from "../types";
import { useCompanies } from "@/features/group_finance/hooks/useGroupFinance";
import { ReservationCalendar } from "../components/ReservationCalendar";

const MANAGER_ROLES = ["director", "coordinator"];

// ── Helpers ───────────────────────────────────────────────────────────────────

function useUsers() {
  const [users, setUsers] = useState<{ id: string; name: string }[]>([]);
  const [loaded, setLoaded] = useState(false);
  const load = async () => {
    if (loaded) return;
    try {
      const { default: apiClient } = await import("@/api/client");
      const res = await apiClient.get<{
        data: { id: string; first_name: string; last_name: string }[];
      }>("/users/");
      setUsers(
        res.data.data.map((u) => ({
          id: u.id,
          name: `${u.first_name} ${u.last_name}`.trim(),
        })),
      );
      setLoaded(true);
    } catch {
      setLoaded(true);
    }
  };
  return { users, load };
}

function extractError(err: unknown): string {
  const e = err as {
    response?: { data?: { errors?: Record<string, unknown> } };
  };
  const errs = e?.response?.data?.errors;
  if (!errs) return "Błąd. Spróbuj ponownie.";
  if (typeof errs.detail === "string") return errs.detail;
  return Object.values(errs).flat().join(" ");
}

/** Returns days until date (negative if expired). */
function daysUntil(dateStr: string | null): number | null {
  if (!dateStr) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(dateStr);
  d.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - today.getTime()) / 86_400_000);
}

function dateUrgency(
  dateStr: string | null,
): "ok" | "warning" | "danger" | null {
  const days = daysUntil(dateStr);
  if (days === null) return null;
  if (days <= 0) return "danger";
  if (days <= 14) return "danger";
  if (days <= 30) return "warning";
  return "ok";
}

const urgencyClasses = {
  ok: "text-green-700 dark:text-green-400",
  warning: "text-amber-600 dark:text-amber-400",
  danger: "text-red-600 dark:text-red-400",
};

function DateRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | null;
}) {
  const urgency = dateUrgency(value);
  if (!value) {
    return (
      <p className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1">
        {icon} {label}: <span className="italic">nie ustawiono</span>
      </p>
    );
  }
  return (
    <p
      className={`text-xs flex items-center gap-1 ${urgencyClasses[urgency ?? "ok"]}`}
    >
      {icon} {label}: <strong>{formatDate(value)}</strong>
      {urgency !== "ok" && daysUntil(value) !== null && (
        <span className="ml-1">
          ({daysUntil(value)! <= 0 ? "po terminie" : `${daysUntil(value)}d`})
        </span>
      )}
    </p>
  );
}

// ── Fleet managers management ─────────────────────────────────────────────────

interface FleetUser {
  id: string;
  first_name: string;
  last_name: string;
  role: string;
  is_active: boolean;
  is_fleet_manager: boolean;
  profile_picture_url?: string | null;
}

function useFleetManagersPanel() {
  const qc = useQueryClient();
  const { data: users = [], isLoading } = useQuery({
    queryKey: ["fleet-manager-users"],
    queryFn: () =>
      apiClient
        .get<{ data: FleetUser[] }>("/auth/users/")
        .then((r) =>
          r.data.data
            .filter((u) => u.role !== "contractor" && u.is_active !== false)
            .sort((a, b) =>
              `${a.last_name} ${a.first_name}`.localeCompare(
                `${b.last_name} ${b.first_name}`,
                "pl",
              ),
            ),
        ),
  });

  const toggle = useMutation({
    mutationFn: ({ id, value }: { id: string; value: boolean }) =>
      apiClient
        .patch<{
          data: FleetUser;
        }>(`/auth/users/${id}/`, { is_fleet_manager: value })
        .then((r) => r.data.data),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["fleet-manager-users"] }),
  });

  return { users, isLoading, toggle };
}

function FleetManagersPanel() {
  const { users, isLoading, toggle } = useFleetManagersPanel();
  const [open, setOpen] = useState(false);

  const managers = users.filter((u) => u.is_fleet_manager);

  return (
    <div className="bg-white dark:bg-gray-800 mb-5">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left"
      >
        {open ? (
          <ChevronDown size={16} className="text-gray-400" />
        ) : (
          <ChevronRight size={16} className="text-gray-400" />
        )}
        <Users size={15} className="text-gray-500" />
        <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
          Opiekunowie floty
        </span>
        {managers.length > 0 && (
          <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
            (
            {managers
              .map((u) => `${u.first_name} ${u.last_name}`.trim())
              .join(", ")}
            )
          </span>
        )}
        {managers.length === 0 && (
          <span className="text-xs text-amber-500 dark:text-amber-400 ml-1">
            brak opiekuna
          </span>
        )}
      </button>

      {open && (
        <div className="border-t border-gray-100 dark:border-gray-700 px-4 py-3">
          {isLoading ? (
            <p className="text-xs text-gray-400">Ładowanie użytkowników...</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {users.map((u) => {
                const isManager = u.is_fleet_manager;
                const isPending =
                  toggle.isPending && toggle.variables?.id === u.id;
                return (
                  <button
                    key={u.id}
                    disabled={isPending}
                    onClick={() =>
                      toggle.mutate({ id: u.id, value: !isManager })
                    }
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border transition-colors ${
                      isManager
                        ? "bg-[var(--color-primary)] text-white border-[var(--color-primary)] hover:bg-[var(--color-primary-hover)]"
                        : "bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-400 border-gray-300 dark:border-gray-600 hover:border-[var(--color-primary)]"
                    } ${isPending ? "opacity-60 cursor-not-allowed" : ""}`}
                  >
                    <UserAvatar
                      userId={u.id}
                      fullName={`${u.first_name} ${u.last_name}`}
                      profilePictureUrl={u.profile_picture_url}
                      size="xs"
                      initialsClassName={
                        isManager
                          ? "bg-white/20 text-white font-bold"
                          : undefined
                      }
                    />
                    {u.first_name} {u.last_name}
                  </button>
                );
              })}
            </div>
          )}
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-3">
            Opiekun floty może zatwierdzać zgłoszenia usterek i edytować daty
            przeglądów/ubezpieczeń.
          </p>
        </div>
      )}
    </div>
  );
}

const ISSUE_STATUS_COLORS: Record<CarIssueStatus, string> = {
  open: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  in_progress:
    "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  resolved:
    "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  rejected: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400",
};

const ISSUE_PRIORITY_COLORS: Record<CarIssuePriority, string> = {
  low: "text-gray-500",
  normal: "text-[var(--color-primary)] dark:text-blue-400",
  high: "text-amber-600 dark:text-amber-400",
  critical: "text-red-600 dark:text-red-400 font-semibold",
};

// ── Main component ────────────────────────────────────────────────────────────

export function FleetPage() {
  const user = useAuthStore((s) => s.user);
  const isManager = !!user && MANAGER_ROLES.includes(user.role);
  const isFleetManager = !!user?.is_fleet_manager;
  const isCeo = !!user?.is_ceo;
  const isInternal = user?.role !== "contractor";

  const { data: cars = [], isLoading } = useCars();
  const { data: inactiveCars = [] } = useCars({ active: "false" });
  const soldCars = isFleetManager ? inactiveCars.filter((c) => c.current_location === "sold") : [];
  const [soldViewCar, setSoldViewCar] = useState<Car | null>(null);

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  // ── Delete car ───────────────────────────────────────────────────────────
  const doDeleteCar = useDeleteCar();
  const handleDeleteCar = (car: Car) => {
    if (!window.confirm(`Usunąć samochód ${car.display_name}?`)) return;
    doDeleteCar.mutate(car.id);
  };

  // ── Sell car ─────────────────────────────────────────────────────────────
  const doSellCar = useSellCar();
  const handleSellCar = (car: Car) => {
    if (!window.confirm(`Oznaczyć ${car.display_name} jako sprzedany? Samochód zniknie z listy floty.`)) return;
    doSellCar.mutate(car.id);
  };

  // ── Edit car modal ───────────────────────────────────────────────────────
  const [editCar, setEditCar] = useState<Car | null>(null);
  const [editForm, setEditForm] = useState<Partial<Car>>({});
  const [editErr, setEditErr] = useState<Record<string, string>>({});
  const doUpdateCar = useUpdateCar();
  const { data: companies = [] } = useCompanies(
    isCeo || user?.role === "accountant" || !!user?.has_group_finance_access,
  );

  const openEdit = (car: Car) => {
    setEditCar(car);
    setEditForm({
      brand: car.brand,
      model: car.model,
      license_plate: car.license_plate,
      year: car.year ?? undefined,
      color: car.color,
      vin: car.vin,
      notes: car.notes,
      first_registration_date: car.first_registration_date ?? undefined,
      euro_category: car.euro_category || undefined,
      fuel_type: car.fuel_type || undefined,
      car_category: car.car_category || undefined,
      ownership_type: car.ownership_type || undefined,
      company: car.company ?? undefined,
    });
    setEditErr({});
  };

  const submitEdit = () => {
    if (!editCar) return;
    const errs: Record<string, string> = {};
    if (!String(editForm.brand ?? "").trim()) errs.brand = "Wymagane";
    if (!String(editForm.model ?? "").trim()) errs.model = "Wymagane";
    if (!String(editForm.license_plate ?? "").trim()) errs.license_plate = "Wymagane";
    if (Object.keys(errs).length) { setEditErr(errs); return; }
    doUpdateCar.mutate(
      { id: editCar.id, data: editForm as Partial<Car> },
      {
        onSuccess: () => { setEditCar(null); setEditErr({}); },
        onError: (err) => setEditErr({ submit: extractError(err) }),
      },
    );
  };

  // ── Create car modal ──────────────────────────────────────────────────────
  const [createOpen, setCreateOpen] = useState(false);
  const [carForm, setCarForm] = useState({
    brand: "",
    model: "",
    license_plate: "",
    year: "",
    color: "",
    vin: "",
    notes: "",
  });
  const [carFormErr, setCarFormErr] = useState<Record<string, string>>({});
  const createCar = useCreateCar();

  const submitCreateCar = () => {
    const errs: Record<string, string> = {};
    if (!carForm.brand.trim()) errs.brand = "Wymagane";
    if (!carForm.model.trim()) errs.model = "Wymagane";
    if (!carForm.license_plate.trim()) errs.license_plate = "Wymagane";
    if (Object.keys(errs).length) {
      setCarFormErr(errs);
      return;
    }
    createCar.mutate(
      {
        brand: carForm.brand.trim(),
        model: carForm.model.trim(),
        license_plate: carForm.license_plate.trim().toUpperCase(),
        year: carForm.year ? parseInt(carForm.year) : undefined,
        color: carForm.color.trim(),
        vin: carForm.vin.trim(),
        notes: carForm.notes.trim(),
      },
      {
        onSuccess: () => {
          setCreateOpen(false);
          setCarForm({
            brand: "",
            model: "",
            license_plate: "",
            year: "",
            color: "",
            vin: "",
            notes: "",
          });
          setCarFormErr({});
        },
        onError: (err) => setCarFormErr({ submit: extractError(err) }),
      },
    );
  };

  // ── Assign modal ──────────────────────────────────────────────────────────
  const [assignCar, setAssignCar] = useState<Car | null>(null);
  const [assignForm, setAssignForm] = useState({
    holder_id: "",
    date_from: "",
    date_to: "",
    notes: "",
  });
  const [assignErr, setAssignErr] = useState("");
  const doAssign = useAssignCar();
  const { users, load: loadUsers } = useUsers();

  const openAssign = (car: Car) => {
    setAssignCar(car);
    setAssignForm({
      holder_id: "",
      date_from: new Date().toISOString().slice(0, 10),
      date_to: "",
      notes: "",
    });
    setAssignErr("");
    loadUsers();
  };

  const submitAssign = () => {
    if (!assignCar) return;
    if (!assignForm.holder_id) {
      setAssignErr("Wybierz użytkownika.");
      return;
    }
    if (!assignForm.date_from) {
      setAssignErr("Podaj datę od.");
      return;
    }
    setAssignErr("");
    doAssign.mutate(
      {
        id: assignCar.id,
        data: {
          holder_id: assignForm.holder_id,
          date_from: assignForm.date_from,
          date_to: assignForm.date_to || undefined,
          notes: assignForm.notes,
        },
      },
      {
        onSuccess: () => setAssignCar(null),
        onError: (err) => setAssignErr(extractError(err)),
      },
    );
  };

  // ── Return to pool modal ──────────────────────────────────────────────────
  const [returnCar, setReturnCar] = useState<Car | null>(null);
  const [returnForm, setReturnForm] = useState({
    pool_location: "pool_opole" as "pool_opole" | "pool_wroclaw",
    date: "",
    notes: "",
  });
  const [returnErr, setReturnErr] = useState("");
  const doReturn = useReturnToPool();

  const openReturn = (car: Car) => {
    setReturnCar(car);
    setReturnForm({ pool_location: "pool_opole", date: "", notes: "" });
    setReturnErr("");
  };

  const submitReturn = () => {
    if (!returnCar) return;
    setReturnErr("");
    doReturn.mutate(
      {
        id: returnCar.id,
        data: {
          pool_location: returnForm.pool_location,
          date: returnForm.date || undefined,
          notes: returnForm.notes,
        },
      },
      {
        onSuccess: () => setReturnCar(null),
        onError: (err) => setReturnErr(extractError(err)),
      },
    );
  };

  // ── Move pool modal ───────────────────────────────────────────────────────
  const [moveCar, setMoveCar] = useState<Car | null>(null);
  const [moveTarget, setMoveTarget] = useState<"pool_opole" | "pool_wroclaw">(
    "pool_wroclaw",
  );
  const [moveErr, setMoveErr] = useState("");
  const doMove = useMovePool();

  const openMove = (car: Car) => {
    setMoveCar(car);
    setMoveTarget(
      car.current_location === "pool_opole" ? "pool_wroclaw" : "pool_opole",
    );
    setMoveErr("");
  };

  const submitMove = () => {
    if (!moveCar) return;
    setMoveErr("");
    doMove.mutate(
      { id: moveCar.id, data: { pool_location: moveTarget } },
      {
        onSuccess: () => setMoveCar(null),
        onError: (err) => setMoveErr(extractError(err)),
      },
    );
  };

  // ── Reservation modal (Wrocław pool) ──────────────────────────────────────
  const [resCar, setResCar] = useState<Car | null>(null);
  const [resRange, setResRange] = useState<{ from: string; to: string } | null>(
    null,
  );
  const [resNotes, setResNotes] = useState("");
  const [resConfirmOpen, setResConfirmOpen] = useState(false);
  const [resErr, setResErr] = useState("");
  const doCreateRes = useCreateReservation();
  const doDeleteRes = useDeleteReservation();
  const { data: allReservations = [] } = useReservations();

  const openReservationModal = (car: Car) => {
    setResCar(car);
    setResRange(null);
    setResNotes("");
    setResErr("");
  };

  const handleSelectRange = (from: string, to: string) => {
    setResRange({ from, to });
    setResNotes("");
    setResErr("");
    setResConfirmOpen(true);
  };

  const submitReservation = () => {
    if (!resCar || !resRange) return;
    setResErr("");
    doCreateRes.mutate(
      {
        car: resCar.id,
        date_from: resRange.from,
        date_to: resRange.to,
        notes: resNotes,
      },
      {
        onSuccess: () => {
          setResConfirmOpen(false);
          setResRange(null);
        },
        onError: (err) => setResErr(extractError(err)),
      },
    );
  };

  const handleDeleteReservation = (res: CarReservation) => {
    if (
      window.confirm(`Anulować rezerwację ${formatDate(res.date_from)} – ${formatDate(res.date_to)}?`)
    ) {
      doDeleteRes.mutate(res.id);
    }
  };

  // ── Report issue modal ────────────────────────────────────────────────────
  const [reportCar, setReportCar] = useState<Car | null>(null);
  const [issueForm, setIssueForm] = useState({
    title: "",
    description: "",
    priority: "normal" as CarIssuePriority,
  });
  const [issueErr, setIssueErr] = useState("");
  const doCreateIssue = useCreateIssueReport();

  const openReportIssue = (car: Car) => {
    setReportCar(car);
    setIssueForm({ title: "", description: "", priority: "normal" });
    setIssueErr("");
  };

  const submitIssue = () => {
    if (!reportCar) return;
    if (!issueForm.title.trim()) {
      setIssueErr("Podaj tytuł zgłoszenia.");
      return;
    }
    setIssueErr("");
    doCreateIssue.mutate(
      {
        car: reportCar.id,
        title: issueForm.title.trim(),
        description: issueForm.description.trim(),
        priority: issueForm.priority,
      },
      {
        onSuccess: () => setReportCar(null),
        onError: (err) => setIssueErr(extractError(err)),
      },
    );
  };

  // ── Issue thread modal ────────────────────────────────────────────────────
  const [threadIssue, setThreadIssue] = useState<CarIssueReport | null>(null);

  // Deep link z powiadomienia o komentarzu / oznaczeniu: ?issue=<uuid> otwiera wątek
  // tego zgłoszenia. Parametr zdejmujemy po otwarciu, żeby zamknięcie modala nie
  // wracało do niego przy kolejnym renderze.
  const [searchParams, setSearchParams] = useSearchParams();
  const deepIssueId = searchParams.get("issue");
  const { data: deepLinkIssues = [] } = useIssueReports(
    deepIssueId ? {} : undefined,
  );
  useEffect(() => {
    if (!deepIssueId) return;
    const found = deepLinkIssues.find((i) => i.id === deepIssueId);
    if (!found) return;
    setThreadIssue(found);
    const next = new URLSearchParams(searchParams);
    next.delete("issue");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepIssueId, deepLinkIssues]);

  return (
    <div>
      <div className="flex items-start justify-between mb-5">
        <div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Flota samochodów
            </h1>
            <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-6" />
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Zarządzaj przydzieleniem i rezerwacjami samochodów firmowych.
          </p>
        </div>
        {isManager && (
          <Button onClick={() => setCreateOpen(true)}>
            <Plus size={15} className="mr-1.5" /> Dodaj samochód
          </Button>
        )}
      </div>

      {/* Fleet managers panel — directors only */}
      {user?.role === "director" && <FleetManagersPanel />}

      {/* Summary badges */}
      {!isLoading && cars.length > 0 && (
        <div className="flex flex-wrap gap-3 mb-5">
          {(["pool_opole", "pool_wroclaw", "assigned"] as const).map((loc) => {
            const count = cars.filter((c) => c.current_location === loc).length;
            if (count === 0) return null;
            return (
              <div
                key={loc}
                className="flex items-center gap-2 bg-white dark:bg-gray-800 px-3 py-2"
              >
                <Badge color={LOCATION_COLORS[loc]}>
                  {LOCATION_LABELS[loc]}
                </Badge>
                <span className="font-semibold text-gray-800 dark:text-gray-200 text-sm">
                  {count}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {isLoading ? (
        <PageLoader />
      ) : cars.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 p-10 text-center">
          <CarIcon
            size={36}
            className="text-gray-300 dark:text-gray-600 mx-auto mb-3"
          />
          <p className="text-gray-400 dark:text-gray-500 text-sm">
            Brak samochodów w systemie.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {cars.map((car) => (
            <CarCard
              key={car.id}
              car={car}
              expanded={expanded.has(car.id)}
              onToggle={() => toggle(car.id)}
              isManager={isManager}
              isFleetManager={isFleetManager}
              isInternal={!!isInternal}
              currentUserId={user?.id ?? ""}
              allReservations={allReservations.filter((r) => r.car === car.id)}
              onAssign={openAssign}
              onReturn={openReturn}
              onMovePool={openMove}
              onReserve={openReservationModal}
              onDeleteReservation={handleDeleteReservation}
              onReportIssue={openReportIssue}
              onOpenThread={setThreadIssue}
              onDeleteCar={handleDeleteCar}
              onSell={handleSellCar}
              onEdit={openEdit}
            />
          ))}
        </div>
      )}

      {/* ── Sold cars — fleet manager only ── */}
      {isFleetManager && soldCars.length > 0 && (
        <div className="mt-8">
          <p className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2 px-1">
            Sprzedane ({soldCars.length})
          </p>
          <div className="flex flex-col divide-y divide-gray-100 dark:divide-gray-700/50 border border-gray-100 dark:border-gray-700/50 rounded-lg overflow-hidden">
            {soldCars.map((car) => (
              <button
                key={car.id}
                type="button"
                onClick={() => setSoldViewCar(car)}
                className="flex items-center gap-3 px-4 py-2.5 bg-white dark:bg-gray-800 opacity-50 hover:opacity-70 transition-opacity text-left w-full"
              >
                <span className="text-sm text-gray-400 dark:text-gray-500 font-medium min-w-0 flex-1">
                  {car.brand} {car.model}
                  <span className="ml-2 font-mono text-xs">{car.license_plate}</span>
                </span>
                <span className="text-xs text-gray-400 dark:text-gray-500 italic flex-shrink-0">Sprzedany</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Sold car readonly modal ── */}
      {soldViewCar && (
        <Modal open onClose={() => setSoldViewCar(null)} title={`${soldViewCar.brand} ${soldViewCar.model} — dane pojazdu`} size="md">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            {[
              ["Nr rejestracyjny", soldViewCar.license_plate],
              ["Rok produkcji", soldViewCar.year],
              ["Kolor", soldViewCar.color],
              ["VIN", soldViewCar.vin],
              ["Data 1. rejestracji", formatDate(soldViewCar.first_registration_date, "")],
              ["Kategoria", soldViewCar.car_category ? CAR_CATEGORY_LABELS[soldViewCar.car_category as keyof typeof CAR_CATEGORY_LABELS] : null],
              ["Paliwo", soldViewCar.fuel_type ? FUEL_TYPE_LABELS[soldViewCar.fuel_type as keyof typeof FUEL_TYPE_LABELS] : null],
              ["EURO", soldViewCar.euro_category ? EURO_CATEGORY_LABELS[soldViewCar.euro_category as keyof typeof EURO_CATEGORY_LABELS] : null],
              ["Forma własności", soldViewCar.ownership_type ? OWNERSHIP_TYPE_LABELS[soldViewCar.ownership_type as keyof typeof OWNERSHIP_TYPE_LABELS] : null],
              ["Spółka", soldViewCar.company_name],
              ["Przegląd do", formatDate(soldViewCar.inspection_due, "")],
              ["Ubezpieczenie do", formatDate(soldViewCar.insurance_due, "")],
            ].map(([label, value]) => value != null && value !== "" ? (
              <div key={String(label)}>
                <p className="text-xs text-gray-400 dark:text-gray-500">{label}</p>
                <p className="font-medium text-gray-800 dark:text-gray-200">{String(value)}</p>
              </div>
            ) : null)}
            {soldViewCar.notes && (
              <div className="col-span-2">
                <p className="text-xs text-gray-400 dark:text-gray-500">Uwagi</p>
                <p className="font-medium text-gray-800 dark:text-gray-200">{soldViewCar.notes}</p>
              </div>
            )}
          </div>
          <div className="mt-4 flex justify-end">
            <Button variant="secondary" size="sm" onClick={() => setSoldViewCar(null)}>Zamknij</Button>
          </div>
        </Modal>
      )}

      {/* ── Edit car modal ── */}
      <Modal open={!!editCar} onClose={() => setEditCar(null)} title={`Edytuj: ${editCar?.display_name ?? ""}`} size="lg">
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Marka *</label>
              <input value={editForm.brand ?? ""} onChange={(e) => setEditForm((f) => ({ ...f, brand: e.target.value }))}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1.5 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]" />
              {editErr.brand && <p className="text-xs text-red-500 mt-0.5">{editErr.brand}</p>}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Model *</label>
              <input value={editForm.model ?? ""} onChange={(e) => setEditForm((f) => ({ ...f, model: e.target.value }))}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1.5 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]" />
              {editErr.model && <p className="text-xs text-red-500 mt-0.5">{editErr.model}</p>}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Nr rejestracyjny *</label>
              <input value={editForm.license_plate ?? ""} onChange={(e) => setEditForm((f) => ({ ...f, license_plate: e.target.value.toUpperCase() }))}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1.5 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]" />
              {editErr.license_plate && <p className="text-xs text-red-500 mt-0.5">{editErr.license_plate}</p>}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Rok produkcji</label>
              <input type="number" min="1900" max="2100" value={editForm.year ?? ""} onChange={(e) => setEditForm((f) => ({ ...f, year: e.target.value ? parseInt(e.target.value) : undefined }))}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1.5 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Data pierwszej rejestracji</label>
              <DatePicker value={editForm.first_registration_date ?? ""} onChange={(v) => setEditForm((f) => ({ ...f, first_registration_date: v || undefined }))} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Kolor</label>
              <input value={editForm.color ?? ""} onChange={(e) => setEditForm((f) => ({ ...f, color: e.target.value }))}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1.5 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">VIN</label>
              <input value={editForm.vin ?? ""} onChange={(e) => setEditForm((f) => ({ ...f, vin: e.target.value }))}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1.5 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Kategoria pojazdu</label>
              <select value={editForm.car_category ?? ""} onChange={(e) => setEditForm((f) => ({ ...f, car_category: e.target.value as Car["car_category"] }))}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1.5 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]">
                <option value="">— wybierz —</option>
                {Object.entries(CAR_CATEGORY_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Rodzaj paliwa</label>
              <select value={editForm.fuel_type ?? ""} onChange={(e) => setEditForm((f) => ({ ...f, fuel_type: e.target.value as Car["fuel_type"] }))}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1.5 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]">
                <option value="">— wybierz —</option>
                {Object.entries(FUEL_TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Kategoria EURO</label>
              <select value={editForm.euro_category ?? ""} onChange={(e) => setEditForm((f) => ({ ...f, euro_category: e.target.value as Car["euro_category"] }))}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1.5 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]">
                <option value="">— wybierz —</option>
                {Object.entries(EURO_CATEGORY_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Forma własności</label>
              <select value={editForm.ownership_type ?? ""} onChange={(e) => setEditForm((f) => ({ ...f, ownership_type: e.target.value as Car["ownership_type"] }))}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1.5 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]">
                <option value="">— wybierz —</option>
                {Object.entries(OWNERSHIP_TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Spółka</label>
              <select value={editForm.company ?? ""} onChange={(e) => setEditForm((f) => ({ ...f, company: e.target.value || null }))}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1.5 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]">
                <option value="">— brak —</option>
                {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Uwagi</label>
            <textarea rows={2} value={editForm.notes ?? ""} onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1.5 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] resize-none" />
          </div>
          {editErr.submit && <p className="text-sm text-red-600">{editErr.submit}</p>}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={() => setEditCar(null)}>Anuluj</Button>
            <Button size="sm" loading={doUpdateCar.isPending} onClick={submitEdit}>Zapisz zmiany</Button>
          </div>
        </div>
      </Modal>

      {/* ── Add car modal ── */}
      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Dodaj samochód"
      >
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Marka <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
                placeholder="np. Toyota"
                value={carForm.brand}
                onChange={(e) =>
                  setCarForm((f) => ({ ...f, brand: e.target.value }))
                }
              />
              {carFormErr.brand && (
                <p className="text-xs text-red-500 mt-1">{carFormErr.brand}</p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Model <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
                placeholder="np. Corolla"
                value={carForm.model}
                onChange={(e) =>
                  setCarForm((f) => ({ ...f, model: e.target.value }))
                }
              />
              {carFormErr.model && (
                <p className="text-xs text-red-500 mt-1">{carFormErr.model}</p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Nr rejestracyjny <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm font-mono uppercase"
                placeholder="np. OP12345"
                value={carForm.license_plate}
                onChange={(e) =>
                  setCarForm((f) => ({
                    ...f,
                    license_plate: e.target.value.toUpperCase(),
                  }))
                }
              />
              {carFormErr.license_plate && (
                <p className="text-xs text-red-500 mt-1">
                  {carFormErr.license_plate}
                </p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Rok produkcji
              </label>
              <input
                type="number"
                min="2000"
                max="2030"
                className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
                placeholder="np. 2022"
                value={carForm.year}
                onChange={(e) =>
                  setCarForm((f) => ({ ...f, year: e.target.value }))
                }
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Kolor
              </label>
              <input
                type="text"
                className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
                placeholder="np. Biały"
                value={carForm.color}
                onChange={(e) =>
                  setCarForm((f) => ({ ...f, color: e.target.value }))
                }
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                VIN
              </label>
              <input
                type="text"
                className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm font-mono"
                value={carForm.vin}
                onChange={(e) =>
                  setCarForm((f) => ({ ...f, vin: e.target.value }))
                }
              />
            </div>
            <div className="col-span-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Uwagi
              </label>
              <textarea
                rows={2}
                className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
                value={carForm.notes}
                onChange={(e) =>
                  setCarForm((f) => ({ ...f, notes: e.target.value }))
                }
              />
            </div>
          </div>
          <p className="text-xs text-gray-400 dark:text-gray-500">
            Samochód zostanie dodany na <strong>Parking Opole</strong> (domyślna
            lokalizacja).
          </p>
          {carFormErr.submit && (
            <p className="text-sm text-red-600">{carFormErr.submit}</p>
          )}
          <div className="flex gap-3">
            <Button loading={createCar.isPending} onClick={submitCreateCar}>
              Dodaj samochód
            </Button>
            <Button variant="secondary" onClick={() => setCreateOpen(false)}>
              Anuluj
            </Button>
          </div>
        </div>
      </Modal>

      {/* ── Assign modal ── */}
      <Modal
        open={!!assignCar}
        onClose={() => setAssignCar(null)}
        title={`Przydziel: ${assignCar?.display_name}`}
      >
        <div className="flex flex-col gap-4">
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Użytkownik <span className="text-red-500">*</span>
            </label>
            <select
              className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
              value={assignForm.holder_id}
              onChange={(e) =>
                setAssignForm((f) => ({ ...f, holder_id: e.target.value }))
              }
            >
              <option value="">— wybierz —</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Od <span className="text-red-500">*</span>
              </label>
              <DatePicker
                value={assignForm.date_from}
                onChange={(v) =>
                  setAssignForm((f) => ({ ...f, date_from: v }))
                }
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Do <span className="text-gray-400 text-xs">(opcjonalnie)</span>
              </label>
              <DatePicker
                value={assignForm.date_to}
                onChange={(v) =>
                  setAssignForm((f) => ({ ...f, date_to: v }))
                }
                min={assignForm.date_from}
              />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Uwagi
            </label>
            <textarea
              rows={2}
              className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
              value={assignForm.notes}
              onChange={(e) =>
                setAssignForm((f) => ({ ...f, notes: e.target.value }))
              }
            />
          </div>
          {assignErr && <p className="text-sm text-red-600">{assignErr}</p>}
          <div className="flex gap-3">
            <Button loading={doAssign.isPending} onClick={submitAssign}>
              Przydziel
            </Button>
            <Button variant="secondary" onClick={() => setAssignCar(null)}>
              Anuluj
            </Button>
          </div>
        </div>
      </Modal>

      {/* ── Return to pool modal ── */}
      <Modal
        open={!!returnCar}
        onClose={() => setReturnCar(null)}
        title={`Zwróć do parkingu: ${returnCar?.display_name}`}
      >
        <div className="flex flex-col gap-4">
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Docelowy parking <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-3">
              {(["pool_opole", "pool_wroclaw"] as const).map((loc) => (
                <label
                  key={loc}
                  className={`flex items-center gap-2 border rounded-lg px-4 py-3 cursor-pointer flex-1 ${returnForm.pool_location === loc ? "border-[var(--color-primary)] bg-[var(--color-primary-dim)] dark:bg-[var(--color-primary-hover)]" : "border-gray-300 dark:border-gray-600"}`}
                >
                  <input
                    type="radio"
                    name="pool_location"
                    value={loc}
                    checked={returnForm.pool_location === loc}
                    onChange={() =>
                      setReturnForm((f) => ({ ...f, pool_location: loc }))
                    }
                    className="sr-only"
                  />
                  <MapPin
                    size={16}
                    className={
                      returnForm.pool_location === loc
                        ? "text-[var(--color-primary)]"
                        : "text-gray-400"
                    }
                  />
                  <span className="text-sm font-medium">
                    {LOCATION_LABELS[loc]}
                  </span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Data zwrotu{" "}
              <span className="text-gray-400 text-xs">(domyślnie: dziś)</span>
            </label>
            <DatePicker
              value={returnForm.date}
              onChange={(v) =>
                setReturnForm((f) => ({ ...f, date: v }))
              }
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Uwagi
            </label>
            <textarea
              rows={2}
              className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
              value={returnForm.notes}
              onChange={(e) =>
                setReturnForm((f) => ({ ...f, notes: e.target.value }))
              }
            />
          </div>
          {returnErr && <p className="text-sm text-red-600">{returnErr}</p>}
          <div className="flex gap-3">
            <Button loading={doReturn.isPending} onClick={submitReturn}>
              Zwróć
            </Button>
            <Button variant="secondary" onClick={() => setReturnCar(null)}>
              Anuluj
            </Button>
          </div>
        </div>
      </Modal>

      {/* ── Move pool modal ── */}
      <Modal
        open={!!moveCar}
        onClose={() => setMoveCar(null)}
        title={`Przenieś między parkingami: ${moveCar?.display_name}`}
      >
        <div className="flex flex-col gap-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Aktualnie:{" "}
            <strong>
              {moveCar ? LOCATION_LABELS[moveCar.current_location] : ""}
            </strong>
          </p>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Docelowy parking
            </label>
            <div className="flex gap-3">
              {(["pool_opole", "pool_wroclaw"] as const).map((loc) => (
                <label
                  key={loc}
                  className={`flex items-center gap-2 border rounded-lg px-4 py-3 cursor-pointer flex-1 ${moveTarget === loc ? "border-[var(--color-primary)] bg-[var(--color-primary-dim)] dark:bg-[var(--color-primary-hover)]" : "border-gray-300 dark:border-gray-600"} ${moveCar?.current_location === loc ? "opacity-40 cursor-not-allowed" : ""}`}
                >
                  <input
                    type="radio"
                    name="move_target"
                    value={loc}
                    checked={moveTarget === loc}
                    disabled={moveCar?.current_location === loc}
                    onChange={() => setMoveTarget(loc)}
                    className="sr-only"
                  />
                  <MapPin
                    size={16}
                    className={
                      moveTarget === loc
                        ? "text-[var(--color-primary)]"
                        : "text-gray-400"
                    }
                  />
                  <span className="text-sm font-medium">
                    {LOCATION_LABELS[loc]}
                  </span>
                </label>
              ))}
            </div>
          </div>
          {moveErr && <p className="text-sm text-red-600">{moveErr}</p>}
          <div className="flex gap-3">
            <Button loading={doMove.isPending} onClick={submitMove}>
              <ArrowLeftRight size={14} className="mr-1.5" />
              Przenieś
            </Button>
            <Button variant="secondary" onClick={() => setMoveCar(null)}>
              Anuluj
            </Button>
          </div>
        </div>
      </Modal>

      {/* ── Reservation calendar modal ── */}
      <Modal
        open={!!resCar}
        onClose={() => {
          setResCar(null);
          setResConfirmOpen(false);
        }}
        title={`Rezerwacja: ${resCar?.display_name}`}
      >
        <div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Kliknij datę startową, potem datę końcową aby zarezerwować samochód.
          </p>
          {resCar && (
            <ReservationCalendar
              reservations={allReservations.filter((r) => r.car === resCar.id)}
              currentUserId={user?.id ?? ""}
              isManager={isManager}
              onSelectRange={handleSelectRange}
              onDeleteReservation={handleDeleteReservation}
              carIsElsewhere={resCar.current_location !== "pool_wroclaw"}
              carLocationLabel={resCar.current_location_display}
            />
          )}
        </div>
      </Modal>

      {/* ── Reservation confirm modal ── */}
      <Modal
        open={resConfirmOpen}
        onClose={() => setResConfirmOpen(false)}
        title="Potwierdź rezerwację"
      >
        <div className="flex flex-col gap-4">
          {resRange && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg px-4 py-3 text-sm">
              <p className="font-semibold text-green-900 dark:text-green-200">
                {resCar?.display_name}
              </p>
              <p className="text-green-700 dark:text-green-400 mt-0.5">
                {formatDate(resRange.from)} – {formatDate(resRange.to)}
              </p>
            </div>
          )}
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Cel / uwagi{" "}
              <span className="text-gray-400 text-xs">(opcjonalnie)</span>
            </label>
            <textarea
              rows={2}
              className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
              placeholder="np. Wyjazd na budowę, spotkanie z klientem..."
              value={resNotes}
              onChange={(e) => setResNotes(e.target.value)}
            />
          </div>
          {resErr && <p className="text-sm text-red-600">{resErr}</p>}
          <div className="flex gap-3">
            <Button loading={doCreateRes.isPending} onClick={submitReservation}>
              <CalendarDays size={14} className="mr-1.5" />
              Zarezerwuj
            </Button>
            <Button
              variant="secondary"
              onClick={() => setResConfirmOpen(false)}
            >
              Anuluj
            </Button>
          </div>
        </div>
      </Modal>

      {/* ── Report issue modal ── */}
      <Modal
        open={!!reportCar}
        onClose={() => setReportCar(null)}
        title={`Zgłoś problem: ${reportCar?.display_name}`}
      >
        <div className="flex flex-col gap-4">
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Tytuł <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
              placeholder="Krótki opis problemu..."
              value={issueForm.title}
              onChange={(e) =>
                setIssueForm((f) => ({ ...f, title: e.target.value }))
              }
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Opis <span className="text-gray-400 text-xs">(opcjonalnie)</span>
            </label>
            <textarea
              rows={3}
              className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
              placeholder="Szczegółowy opis usterki lub koniecznych prac..."
              value={issueForm.description}
              onChange={(e) =>
                setIssueForm((f) => ({ ...f, description: e.target.value }))
              }
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Priorytet
            </label>
            <select
              className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
              value={issueForm.priority}
              onChange={(e) =>
                setIssueForm((f) => ({
                  ...f,
                  priority: e.target.value as CarIssuePriority,
                }))
              }
            >
              {(
                Object.entries(ISSUE_PRIORITY_LABELS) as [
                  CarIssuePriority,
                  string,
                ][]
              ).map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </select>
          </div>
          {issueErr && <p className="text-sm text-red-600">{issueErr}</p>}
          <div className="flex gap-3">
            <Button loading={doCreateIssue.isPending} onClick={submitIssue}>
              <ClipboardList size={14} className="mr-1.5" /> Zgłoś problem
            </Button>
            <Button variant="secondary" onClick={() => setReportCar(null)}>
              Anuluj
            </Button>
          </div>
        </div>
      </Modal>

      {/* ── Fleet summary — CEO and fleet manager only ── */}
      {(isCeo || isFleetManager) && (
        <FleetSummary
          cars={cars}
          isFleetManager={isFleetManager}
          onOpenThread={setThreadIssue}
        />
      )}

      {/* ── Issue thread modal ── */}
      {threadIssue && (
        <IssueThreadModal
          issue={threadIssue}
          isFleetManager={isFleetManager}
          currentUserId={user?.id ?? ""}
          onClose={() => setThreadIssue(null)}
          onIssueUpdated={setThreadIssue}
        />
      )}
    </div>
  );
}

// ── Fleet summary ────────────────────────────────────────────────────────────

interface FleetSummaryProps {
  cars: Car[];
  isFleetManager: boolean;
  onOpenThread: (issue: CarIssueReport) => void;
}

function FleetSummary({
  cars,
  isFleetManager,
  onOpenThread,
}: FleetSummaryProps) {
  const { data: allIssues = [] } = useIssueReports({});

  const today = new Date().toISOString().slice(0, 10);
  const threshold = new Date(Date.now() + 30 * 86_400_000)
    .toISOString()
    .slice(0, 10);

  const openIssues = allIssues.filter(
    (i) => i.status === "open" || i.status === "in_progress",
  );
  const expiringCars = cars.filter(
    (c) =>
      (c.inspection_due && c.inspection_due <= threshold) ||
      (c.insurance_due && c.insurance_due <= threshold),
  );

  if (openIssues.length === 0 && expiringCars.length === 0) return null;

  return (
    <div className="mt-6">
      <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wide">
        Podsumowanie — do zrobienia
      </h2>
      <div className="flex gap-4 flex-wrap">
        {/* Open issues */}
        {openIssues.length > 0 && (
          <div className="flex-1 min-w-72 overflow-hidden bg-white dark:bg-gray-800">
            <div className="px-4 py-2.5 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2">
              <AlertTriangle size={13} className="text-red-500" />
              <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                Otwarte zgłoszenia ({openIssues.length})
              </span>
            </div>
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {openIssues.map((issue) => (
                <button
                  key={issue.id}
                  onClick={() => onOpenThread(issue)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <span
                    className={`text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0 ${ISSUE_STATUS_COLORS[issue.status]}`}
                  >
                    {issue.status_display}
                  </span>
                  <span
                    className={`text-[10px] flex-shrink-0 ${ISSUE_PRIORITY_COLORS[issue.priority]}`}
                  >
                    {issue.priority_display}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-800 dark:text-gray-200 truncate">
                      {issue.title}
                    </p>
                    <p className="text-[10px] text-gray-400 dark:text-gray-500">
                      {issue.car_display}
                    </p>
                  </div>
                  {issue.comment_count > 0 && (
                    <span className="flex items-center gap-0.5 text-[10px] text-gray-400 flex-shrink-0">
                      <MessageSquare size={10} /> {issue.comment_count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Expiring inspection/insurance */}
        {expiringCars.length > 0 && (
          <div className="flex-1 min-w-64 overflow-hidden bg-white dark:bg-gray-800">
            <div className="px-4 py-2.5 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2">
              <AlertTriangle size={13} className="text-amber-500" />
              <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                Terminy — poniżej 30 dni ({expiringCars.length}{" "}
                {expiringCars.length === 1 ? "pojazd" : "pojazdy"})
              </span>
            </div>
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {expiringCars.map((car) => {
                const inspUrgency = dateUrgency(car.inspection_due);
                const insUrgency = dateUrgency(car.insurance_due);
                return (
                  <div key={car.id} className="px-4 py-2.5">
                    <p className="text-xs font-medium text-gray-800 dark:text-gray-200">
                      {car.display_name}
                    </p>
                    <div className="flex flex-wrap gap-3 mt-1">
                      {car.inspection_due && inspUrgency !== "ok" && (
                        <span
                          className={`flex items-center gap-1 text-[10px] ${urgencyClasses[inspUrgency ?? "ok"]}`}
                        >
                          <Wrench size={9} /> Przegląd: {formatDate(car.inspection_due)}
                          {daysUntil(car.inspection_due) !== null && (
                            <span>
                              (
                              {daysUntil(car.inspection_due)! <= 0
                                ? "po terminie"
                                : `${daysUntil(car.inspection_due)}d`}
                              )
                            </span>
                          )}
                        </span>
                      )}
                      {car.insurance_due && insUrgency !== "ok" && (
                        <span
                          className={`flex items-center gap-1 text-[10px] ${urgencyClasses[insUrgency ?? "ok"]}`}
                        >
                          <ShieldCheck size={9} /> Ubezp.: {formatDate(car.insurance_due)}
                          {daysUntil(car.insurance_due) !== null && (
                            <span>
                              (
                              {daysUntil(car.insurance_due)! <= 0
                                ? "po terminie"
                                : `${daysUntil(car.insurance_due)}d`}
                              )
                            </span>
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Issue thread modal ────────────────────────────────────────────────────────

interface IssueThreadModalProps {
  issue: CarIssueReport;
  isFleetManager: boolean;
  currentUserId: string;
  onClose: () => void;
  onIssueUpdated?: (updated: CarIssueReport) => void;
}

function IssueThreadModal({
  issue,
  isFleetManager,
  currentUserId,
  onClose,
  onIssueUpdated,
}: IssueThreadModalProps) {
  const { data: comments = [], isLoading } = useIssueComments(issue.id);
  const doCreate = useCreateComment();
  const doDelete = useDeleteComment();
  const doUpdate = useUpdateIssueReport();
  const { users, load: loadUsers } = useUsers();

  const [localStatus, setLocalStatus] = useState<CarIssueStatus>(issue.status);
  const [localPriority, setLocalPriority] = useState<CarIssuePriority>(
    issue.priority,
  );

  useEffect(() => {
    setLocalStatus(issue.status);
    setLocalPriority(issue.priority);
  }, [issue.status, issue.priority]);

  const [text, setText] = useState("");
  const [mentionedIds, setMentionedIds] = useState<string[]>([]);
  // inline @mention state
  const [mentionQuery, setMentionQuery] = useState<string | null>(null); // null = closed
  const [mentionStart, setMentionStart] = useState(0); // cursor position of the "@"
  const [commentErr, setCommentErr] = useState("");

  const filteredSuggestions =
    mentionQuery !== null
      ? users
          .filter((u) =>
            u.name.toLowerCase().includes(mentionQuery.toLowerCase()),
          )
          .slice(0, 6)
      : [];

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setText(val);
    const cursor = e.target.selectionStart ?? val.length;
    // Find the last "@" before cursor without a space between it and cursor
    const before = val.slice(0, cursor);
    const atMatch = before.match(/@([^\s@]*)$/);
    if (atMatch) {
      setMentionQuery(atMatch[1]);
      setMentionStart(before.lastIndexOf("@"));
      loadUsers();
    } else {
      setMentionQuery(null);
    }
  };

  const insertMention = (user: { id: string; name: string }) => {
    // Replace "@query" fragment with "@FirstName LastName"
    const before = text.slice(0, mentionStart);
    const after = text.slice(mentionStart + 1 + (mentionQuery?.length ?? 0));
    const newText = `${before}@${user.name}${after.startsWith(" ") ? "" : " "}${after}`;
    setText(newText);
    setMentionedIds((prev) =>
      prev.includes(user.id) ? prev : [...prev, user.id],
    );
    setMentionQuery(null);
  };

  const submitComment = () => {
    if (!text.trim()) return;
    setCommentErr("");
    doCreate.mutate(
      { report: issue.id, text: text.trim(), mentioned_users: mentionedIds },
      {
        onSuccess: () => {
          setText("");
          setMentionedIds([]);
        },
        onError: () => setCommentErr("Nie udało się dodać komentarza."),
      },
    );
  };

  const handleStatusChange = (newStatus: CarIssueStatus) => {
    setLocalStatus(newStatus);
    doUpdate.mutate(
      { id: issue.id, data: { status: newStatus } },
      {
        onSuccess: (updated) => onIssueUpdated?.(updated),
        onError: () => setLocalStatus(issue.status),
      },
    );
  };

  const handlePriorityChange = (newPriority: CarIssuePriority) => {
    setLocalPriority(newPriority);
    doUpdate.mutate(
      { id: issue.id, data: { priority: newPriority } },
      {
        onSuccess: (updated) => onIssueUpdated?.(updated),
        onError: () => setLocalPriority(issue.priority),
      },
    );
  };

  return (
    <Modal
      open={true}
      onClose={onClose}
      title={`Zgłoszenie: ${issue.car_display}`}
    >
      <div className="flex flex-col gap-4">
        {/* Issue header */}
        <div className="px-4 py-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-gray-900 dark:text-gray-100">
                {issue.title}
              </p>
              {issue.description && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {issue.description}
                </p>
              )}
            </div>
            <div className="flex flex-col items-end gap-1 flex-shrink-0">
              <span
                className={`text-xs px-2 py-0.5 rounded-full ${ISSUE_STATUS_COLORS[issue.status]}`}
              >
                {issue.status_display}
              </span>
              <span
                className={`text-xs ${ISSUE_PRIORITY_COLORS[issue.priority]}`}
              >
                {issue.priority_display}
              </span>
            </div>
          </div>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
            {issue.reporter_name} ·{" "}
            {new Date(issue.created_at).toLocaleDateString("pl-PL")}
          </p>

          {/* Fleet manager controls */}
          {isFleetManager && (
            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
              <select
                className="text-xs rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1"
                value={localStatus}
                onChange={(e) =>
                  handleStatusChange(e.target.value as CarIssueStatus)
                }
              >
                {(
                  Object.entries(ISSUE_STATUS_LABELS) as [
                    CarIssueStatus,
                    string,
                  ][]
                ).map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </select>
              <select
                className="text-xs rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1"
                value={localPriority}
                onChange={(e) =>
                  handlePriorityChange(e.target.value as CarIssuePriority)
                }
              >
                {(
                  Object.entries(ISSUE_PRIORITY_LABELS) as [
                    CarIssuePriority,
                    string,
                  ][]
                ).map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Comments */}
        <div>
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
            Komentarze ({comments.length})
          </p>
          {isLoading ? (
            <p className="text-xs text-gray-400">Ładowanie...</p>
          ) : comments.length === 0 ? (
            <p className="text-xs text-gray-400 italic">
              Brak komentarzy — bądź pierwszy.
            </p>
          ) : (
            <div className="flex flex-col gap-2 max-h-64 overflow-y-auto pr-1">
              {comments.map((c) => (
                <div key={c.id} className="bg-white dark:bg-gray-800 px-3 py-2">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                      {c.author_name ?? "?"}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400">
                        {new Date(c.created_at).toLocaleString("pl-PL", {
                          dateStyle: "short",
                          timeStyle: "short",
                        })}
                      </span>
                      {(c.author === currentUserId || isFleetManager) && (
                        <button
                          onClick={() =>
                            doDelete.mutate({ id: c.id, reportId: issue.id })
                          }
                          className="text-gray-300 hover:text-red-500 dark:text-gray-600 dark:hover:text-red-400"
                          title="Usuń komentarz"
                        >
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                    {c.text}
                  </p>
                  {c.mentioned_user_names.length > 0 && (
                    <p className="text-xs text-[var(--color-primary)] dark:text-[var(--color-primary)] mt-1">
                      @{c.mentioned_user_names.join(", @")}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* New comment form */}
        <div className="flex flex-col gap-2">
          <div className="relative">
            <textarea
              rows={2}
              className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm resize-none"
              placeholder="Dodaj komentarz… wpisz @ aby oznaczyć osobę"
              value={text}
              onChange={handleTextChange}
              onKeyDown={(e) => {
                if (
                  mentionQuery !== null &&
                  filteredSuggestions.length > 0 &&
                  e.key === "Escape"
                ) {
                  setMentionQuery(null);
                  e.preventDefault();
                } else if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                  submitComment();
                }
              }}
            />
            {/* @mention dropdown */}
            {mentionQuery !== null && filteredSuggestions.length > 0 && (
              <div className="absolute left-0 bottom-full mb-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg overflow-hidden z-10">
                {filteredSuggestions.map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      insertMention(u);
                    }}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-[var(--color-primary-dim)] dark:hover:bg-[var(--color-primary-dim)] flex items-center gap-2"
                  >
                    <User size={12} className="text-gray-400 flex-shrink-0" />
                    {u.name}
                  </button>
                ))}
              </div>
            )}
            {mentionQuery !== null &&
              users.length > 0 &&
              filteredSuggestions.length === 0 && (
                <div className="absolute left-0 bottom-full mb-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg px-3 py-2 text-xs text-gray-400 z-10">
                  Brak wyników dla „@{mentionQuery}"
                </div>
              )}
          </div>
          {mentionedIds.length > 0 && (
            <p className="text-xs text-[var(--color-primary)] dark:text-[var(--color-primary)]">
              Oznaczeni:{" "}
              {users
                .filter((u) => mentionedIds.includes(u.id))
                .map((u) => u.name)
                .join(", ")}
            </p>
          )}
          <div className="flex items-center justify-between">
            <p className="text-[10px] text-gray-400 dark:text-gray-500">
              Wpisz @ aby oznaczyć · Ctrl+Enter aby wysłać
            </p>
            <Button
              size="sm"
              loading={doCreate.isPending}
              onClick={submitComment}
              disabled={!text.trim()}
            >
              <Send size={12} className="mr-1" /> Wyślij
            </Button>
          </div>
          {commentErr && <p className="text-xs text-red-600">{commentErr}</p>}
        </div>
      </div>
    </Modal>
  );
}

// ── Car card component ────────────────────────────────────────────────────────

interface CarCardProps {
  car: Car;
  expanded: boolean;
  onToggle: () => void;
  isManager: boolean;
  isFleetManager: boolean;
  isInternal: boolean;
  currentUserId: string;
  allReservations: CarReservation[];
  onAssign: (car: Car) => void;
  onReturn: (car: Car) => void;
  onMovePool: (car: Car) => void;
  onReserve: (car: Car) => void;
  onDeleteReservation: (res: CarReservation) => void;
  onReportIssue: (car: Car) => void;
  onOpenThread: (issue: CarIssueReport) => void;
  onDeleteCar: (car: Car) => void;
  onSell: (car: Car) => void;
  onEdit: (car: Car) => void;
}

function useDateEdit(carId: string) {
  const updateCar = useUpdateCar();
  const [editing, setEditing] = useState(false);
  const [inspection, setInspection] = useState("");
  const [insurance, setInsurance] = useState("");

  const startEdit = (car: Car) => {
    setInspection(car.inspection_due ?? "");
    setInsurance(car.insurance_due ?? "");
    setEditing(true);
  };
  const cancel = () => setEditing(false);
  const save = () => {
    updateCar.mutate(
      {
        id: carId,
        data: {
          inspection_due: inspection || null,
          insurance_due: insurance || null,
        } as Partial<Car>,
      },
      { onSuccess: () => setEditing(false) },
    );
  };
  return {
    editing,
    inspection,
    insurance,
    setInspection,
    setInsurance,
    startEdit,
    cancel,
    save,
    isPending: updateCar.isPending,
  };
}

function CarCard({
  car,
  expanded,
  onToggle,
  isManager,
  isFleetManager,
  isInternal,
  currentUserId,
  allReservations,
  onAssign,
  onReturn,
  onMovePool,
  onReserve,
  onDeleteReservation,
  onReportIssue,
  onOpenThread,
  onDeleteCar,
  onSell,
  onEdit,
}: CarCardProps) {
  const { data: history = [], isFetching } = useCarHistory(
    expanded ? car.id : "",
  );
  const { data: issues = [] } = useIssueReports(
    expanded ? { car: car.id } : undefined,
  );
  const doUpdateIssue = useUpdateIssueReport();
  const dateEdit = useDateEdit(car.id);

  const upcoming = allReservations
    .filter((r) => r.date_to >= new Date().toISOString().slice(0, 10))
    .slice(0, 3);

  const openIssues = issues.filter(
    (i) => i.status === "open" || i.status === "in_progress",
  );
  const inspectionUrgency = dateUrgency(car.inspection_due);
  const insuranceUrgency = dateUrgency(car.insurance_due);

  const hasAlert =
    inspectionUrgency === "danger" ||
    inspectionUrgency === "warning" ||
    insuranceUrgency === "danger" ||
    insuranceUrgency === "warning";

  const actionButtons = (
    <div className="flex items-center gap-1.5 flex-wrap">
      {isInternal && (
        <button
          onClick={() => onReportIssue(car)}
          className="text-xs bg-orange-50 dark:bg-orange-900/20 border border-orange-300 dark:border-orange-700 text-orange-700 dark:text-orange-400 px-2.5 py-1.5 rounded-md hover:bg-orange-100 font-medium flex items-center gap-1"
        >
          <AlertTriangle size={12} /> Zgłoś problem
        </button>
      )}
      {car.current_location !== "assigned" && (
        <button
          onClick={() => onReserve(car)}
          className={`text-xs px-2.5 py-1.5 rounded-md font-medium flex items-center gap-1 ${
            car.current_location === "pool_wroclaw"
              ? "bg-green-600 text-white hover:bg-green-700"
              : "bg-orange-500 text-white hover:bg-orange-600"
          }`}
        >
          <CalendarDays size={12} /> Rezerwuj
        </button>
      )}
      {isManager && (
        <>
          {car.current_location === "assigned" && (
            <button
              onClick={() => onReturn(car)}
              className="text-xs bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-2.5 py-1.5 rounded-md hover:bg-gray-50 font-medium"
            >
              Zwróć
            </button>
          )}
          {(car.current_location === "pool_opole" ||
            car.current_location === "pool_wroclaw") && (
            <>
              <button
                onClick={() => onAssign(car)}
                className="text-xs bg-[var(--color-primary)] text-white px-2.5 py-1.5 rounded-md hover:bg-[var(--color-primary-hover)] font-medium flex items-center gap-1"
              >
                <User size={12} /> Przydziel
              </button>
              <button
                onClick={() => onMovePool(car)}
                className="text-xs bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 px-2.5 py-1.5 rounded-md hover:bg-gray-50 font-medium flex items-center gap-1"
              >
                <ArrowLeftRight size={12} /> Przenieś
              </button>
            </>
          )}
        </>
      )}
      {(isFleetManager || isManager) && (
        <button
          onClick={() => onEdit(car)}
          className="text-xs bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-2.5 py-1.5 rounded-md hover:bg-gray-50 font-medium flex items-center gap-1"
        >
          <Pencil size={12} /> Edytuj
        </button>
      )}
      {isFleetManager && car.current_location !== "sold" && (
        <button
          onClick={() => onSell(car)}
          className="text-xs bg-white dark:bg-gray-700 border border-orange-300 dark:border-orange-700 text-orange-700 dark:text-orange-400 px-2.5 py-1.5 rounded-md hover:bg-orange-50 dark:hover:bg-orange-900/20 font-medium flex items-center gap-1"
          title="Oznacz jako sprzedany"
        >
          Sprzedane
        </button>
      )}
      {(isManager || isFleetManager) && (
        <button
          onClick={() => onDeleteCar(car)}
          className="text-xs bg-white dark:bg-gray-700 border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 px-2.5 py-1.5 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 font-medium flex items-center gap-1"
          title="Dezaktywuj samochód"
        >
          <Trash2 size={12} /> Usuń
        </button>
      )}
    </div>
  );

  return (
    <div className="bg-white dark:bg-gray-800 overflow-hidden">
      {/* Header row */}
      <div className="flex items-start gap-3 px-4 py-3">
        <button
          onClick={onToggle}
          className="text-gray-400 hover:text-gray-600 flex-shrink-0 mt-0.5"
        >
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-bold text-gray-900 dark:text-gray-100">
                  {car.brand} {car.model}
                </span>
                <span className="font-mono text-sm text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">
                  {car.license_plate}
                </span>
                <Badge color={LOCATION_COLORS[car.current_location]}>
                  {car.current_location_display}
                </Badge>
                {openIssues.length > 0 && (
                  <span className="flex items-center gap-1 text-xs text-red-600 dark:text-red-400 font-medium">
                    <AlertTriangle size={11} /> {openIssues.length}{" "}
                    {openIssues.length === 1 ? "problem" : "problemy"}
                  </span>
                )}
                {hasAlert && (
                  <span className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400 font-medium">
                    <AlertTriangle size={11} /> Termin
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 dark:text-gray-400 flex-wrap">
                {car.current_location === "assigned" && car.current_holder_name && (
                  <span className="flex items-center gap-1">
                    <User size={11} /> {car.current_holder_name}
                  </span>
                )}
                {car.year && <span>{car.year}</span>}
                {car.color && <span>{car.color}</span>}
                {car.vin && <span className="font-mono">VIN: {car.vin}</span>}
                {car.inspection_due && (
                  <span
                    className={`flex items-center gap-1 ${urgencyClasses[inspectionUrgency ?? "ok"]}`}
                  >
                    <Wrench size={10} /> Przegląd: {formatDate(car.inspection_due)}
                    {inspectionUrgency !== "ok" &&
                      daysUntil(car.inspection_due) !== null && (
                        <span>
                          (
                          {daysUntil(car.inspection_due)! <= 0
                            ? "po terminie"
                            : `${daysUntil(car.inspection_due)}d`}
                          )
                        </span>
                      )}
                  </span>
                )}
                {car.insurance_due && (
                  <span
                    className={`flex items-center gap-1 ${urgencyClasses[insuranceUrgency ?? "ok"]}`}
                  >
                    <ShieldCheck size={10} /> Ubezp.: {formatDate(car.insurance_due)}
                    {insuranceUrgency !== "ok" &&
                      daysUntil(car.insurance_due) !== null && (
                        <span>
                          (
                          {daysUntil(car.insurance_due)! <= 0
                            ? "po terminie"
                            : `${daysUntil(car.insurance_due)}d`}
                          )
                        </span>
                      )}
                  </span>
                )}
                {upcoming.length > 0 && car.current_location !== "assigned" && (
                  <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
                    <Calendar size={11} /> {upcoming.length}{" "}
                    {upcoming.length === 1 ? "rezerwacja" : "rezerwacje"}
                  </span>
                )}
              </div>
            </div>
            {/* Desktop actions — inline to the right */}
            <div className="hidden sm:block flex-shrink-0">
              {actionButtons}
            </div>
          </div>
          {/* Mobile actions — full row below car info */}
          <div className="sm:hidden mt-2">
            {actionButtons}
          </div>
        </div>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="border-t border-gray-100 dark:border-gray-700 px-4 py-4">
          <div className="flex gap-6 flex-wrap">
            {/* Assignment history */}
            <div className="flex-1 min-w-48">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2 flex items-center gap-1">
                <History size={11} /> Historia przydzielenia
              </p>
              {isFetching ? (
                <p className="text-xs text-gray-400">Ładowanie...</p>
              ) : history.length === 0 ? (
                <p className="text-xs text-gray-400">Brak wpisów.</p>
              ) : (
                <div className="flex flex-col gap-1.5">
                  {history.slice(0, 8).map((a) => (
                    <div key={a.id} className="text-xs flex items-start gap-2">
                      <Badge
                        color={LOCATION_COLORS[a.location]}
                        className="flex-shrink-0"
                      >
                        {a.location_display}
                      </Badge>
                      <div className="min-w-0">
                        {a.holder_name && (
                          <span className="font-medium text-gray-700 dark:text-gray-300">
                            {a.holder_name} ·{" "}
                          </span>
                        )}
                        <span className="text-gray-500 dark:text-gray-400">
                          {formatDate(a.date_from)}
                          {a.date_to ? ` → ${formatDate(a.date_to)}` : " → nadal"}
                        </span>
                        {a.notes && (
                          <p className="text-gray-400 dark:text-gray-500 italic truncate">
                            {a.notes}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Upcoming reservations */}
            {car.current_location !== "assigned" && (
              <div className="flex-1 min-w-48">
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2 flex items-center gap-1">
                  <Calendar size={11} /> Nadchodzące rezerwacje
                </p>
                {upcoming.length === 0 ? (
                  <p className="text-xs text-gray-400">
                    Brak nadchodzących rezerwacji.
                  </p>
                ) : (
                  <div className="flex flex-col gap-1.5">
                    {upcoming.map((r) => (
                      <div
                        key={r.id}
                        className="text-xs flex items-center gap-2"
                      >
                        <span className="font-medium text-gray-700 dark:text-gray-300">
                          {r.reserved_by_name}
                        </span>
                        <span className="text-gray-500 dark:text-gray-400">
                          {formatDate(r.date_from)} – {formatDate(r.date_to)}
                        </span>
                        {r.notes && (
                          <span className="text-gray-400 italic truncate max-w-[120px]">
                            {r.notes}
                          </span>
                        )}
                        {(r.reserved_by === currentUserId || isManager) && (
                          <button
                            onClick={() => onDeleteReservation(r)}
                            className="ml-auto flex-shrink-0 text-red-400 hover:text-red-600 font-bold text-base leading-none"
                            title="Anuluj rezerwację"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Inspection / insurance dates */}
            {(isFleetManager || isManager) && (
              <div className="flex-1 min-w-48">
                <div className="flex items-center gap-2 mb-2">
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide flex items-center gap-1">
                    <Wrench size={11} /> Terminy
                  </p>
                  {!dateEdit.editing && (
                    <button
                      onClick={() => dateEdit.startEdit(car)}
                      className="text-[10px] text-[var(--color-primary)] dark:text-[var(--color-primary)] hover:underline"
                    >
                      Edytuj
                    </button>
                  )}
                </div>
                {dateEdit.editing ? (
                  <div className="flex flex-col gap-2">
                    <div>
                      <label className="text-[10px] text-gray-500 dark:text-gray-400 block mb-0.5 flex items-center gap-1">
                        <Wrench size={9} /> Przegląd ważny do
                      </label>
                      <DatePicker
                        value={dateEdit.inspection}
                        onChange={dateEdit.setInspection}
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-500 dark:text-gray-400 block mb-0.5 flex items-center gap-1">
                        <ShieldCheck size={9} /> Ubezpieczenie ważne do
                      </label>
                      <DatePicker
                        value={dateEdit.insurance}
                        onChange={dateEdit.setInsurance}
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={dateEdit.save}
                        disabled={dateEdit.isPending}
                        className="text-xs bg-[var(--color-primary)] text-white px-2.5 py-1 rounded hover:bg-[var(--color-primary-hover)] disabled:opacity-60"
                      >
                        {dateEdit.isPending ? "Zapisuję..." : "Zapisz"}
                      </button>
                      <button
                        onClick={dateEdit.cancel}
                        className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 px-1"
                      >
                        Anuluj
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col gap-1">
                    <DateRow
                      icon={<Wrench size={10} />}
                      label="Przegląd"
                      value={car.inspection_due}
                    />
                    <DateRow
                      icon={<ShieldCheck size={10} />}
                      label="Ubezpieczenie"
                      value={car.insurance_due}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Issue reports */}
            <div className="flex-1 min-w-56">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2 flex items-center gap-1">
                <AlertTriangle size={11} /> Zgłoszenia problemów
              </p>
              {issues.length === 0 ? (
                <p className="text-xs text-gray-400">Brak zgłoszeń.</p>
              ) : (
                <div className="flex flex-col gap-1.5">
                  {issues.map((issue) => (
                    <div
                      key={issue.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => onOpenThread(issue)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onOpenThread(issue);
                        }
                      }}
                      className="text-xs px-2.5 py-2 bg-white dark:bg-gray-800 rounded border border-transparent hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className={`text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0 ${ISSUE_STATUS_COLORS[issue.status]}`}
                        >
                          {issue.status_display}
                        </span>
                        <span
                          className={`flex-shrink-0 ${ISSUE_PRIORITY_COLORS[issue.priority]}`}
                        >
                          {issue.priority_display}
                        </span>
                        <span className="font-medium text-gray-700 dark:text-gray-300 truncate flex-1 text-left flex items-center gap-1">
                          {issue.title}
                          {issue.comment_count > 0 && (
                            <span className="flex items-center gap-0.5 text-gray-400 flex-shrink-0">
                              <MessageSquare size={10} /> {issue.comment_count}
                            </span>
                          )}
                        </span>
                        {isFleetManager && (
                          <select
                            className="text-[10px] rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-1 py-0.5 flex-shrink-0"
                            value={issue.status}
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                            onChange={(e) =>
                              doUpdateIssue.mutate({
                                id: issue.id,
                                data: {
                                  status: e.target.value as CarIssueStatus,
                                },
                              })
                            }
                          >
                            {(
                              Object.entries(ISSUE_STATUS_LABELS) as [
                                CarIssueStatus,
                                string,
                              ][]
                            ).map(([v, l]) => (
                              <option key={v} value={v}>
                                {l}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                      <p className="text-gray-400 dark:text-gray-500 mt-0.5">
                        {issue.reporter_name} ·{" "}
                        {new Date(issue.created_at).toLocaleDateString("pl-PL")}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
